// Lazily-built Set of canonical IANA zone names (`Intl.supportedValuesOf`).
// Built once — the list is ~400 entries and never changes within a runtime.
let cachedZones: Set<string> | null = null;
let cacheAttempted = false;

function canonicalZones(): Set<string> | null {
    if (cacheAttempted) return cachedZones;
    cacheAttempted = true;
    const supported = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    if (typeof supported !== 'function') return null;
    try {
        cachedZones = new Set(supported('timeZone'));
    } catch {
        cachedZones = null;
    }
    return cachedZones;
}

/**
 * True if `zone` is an IANA time-zone identifier (e.g. `America/Chicago`).
 * Empty string, abbreviations (`CST`), and unknown identifiers return false.
 *
 * The canonical validity check for the write path: `tenants.time_zone` is an
 * open `TEXT` column (no DB CHECK), so callers validate here before persisting.
 *
 * Prefers the canonical IANA list (`Intl.supportedValuesOf('timeZone')`) so
 * fixed-offset aliases like `CST`/`EST` — which `Intl.DateTimeFormat` would
 * otherwise accept — are rejected. `UTC` is allowed explicitly because the
 * canonical list omits it (it's a fixed zone, not a Region/City entry). Where
 * `supportedValuesOf` is unavailable (older runtimes / some React Native
 * engines), falls back to a construct-and-catch probe (more lenient, but
 * better than nothing).
 *
 * @sideEffect none — pure (memoizes the zone Set internally; same input → same output).
 */
export function isValidTimeZone(zone: string): boolean {
    if (!zone) return false;
    if (zone === 'UTC') return true;
    const zones = canonicalZones();
    if (zones) return zones.has(zone);
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: zone });
        return true;
    } catch {
        return false;
    }
}
